
import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Button,
  Box,
  Paper
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/api';

const GadgetList = () => {
  const [gadgets, setGadgets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();

  useEffect(() => {
    fetchGadgets();
  }, []);

  const fetchGadgets = async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/admin/gadgets');
      if (res.success) {
        setGadgets(res.data);
      } else {
        throw new Error(res.message || 'Failed to fetch gadgets');
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this gadget?')) return;
    try {
      const res = await apiFetch(`/api/admin/gadgets/${id}`, 'DELETE');
      if (res.success) {
        setSnackbar({ open: true, message: 'Gadget deleted', severity: 'success' });
        fetchGadgets();
      } else {
        throw new Error(res.message || 'Delete failed');
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      {/* Header with Back Button */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold" color="primary.dark">
          Gadget Records
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/nomas')}
        >
          Back to Admin Dashboard
        </Button>
      </Box>

      <Paper elevation={3} sx={{ p: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
          </Box>
        ) : gadgets.length === 0 ? (
          <Typography variant="body1" align="center" sx={{ py: 5 }}>
            No gadget records found.
          </Typography>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#0d1b2a' }}>
                <TableCell sx={{ color: 'white' }}>Brand</TableCell>
                <TableCell sx={{ color: 'white' }}>Model</TableCell>
                <TableCell sx={{ color: 'white' }}>Serial / IMEI</TableCell>
                <TableCell sx={{ color: 'white' }}>Type</TableCell>
                <TableCell sx={{ color: 'white' }}>Owner Name</TableCell>
                <TableCell sx={{ color: 'white' }}>Telephone</TableCell>
                <TableCell sx={{ color: 'white' }}>Address</TableCell>
                <TableCell sx={{ color: 'white' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {gadgets.map(gadget => (
                <TableRow key={gadget._id} hover>
                  <TableCell>{gadget.brand}</TableCell>
                  <TableCell>{gadget.model}</TableCell>
                  <TableCell>{gadget.serialNumber || gadget.imei || '-'}</TableCell>
                  <TableCell>{gadget.deviceType}</TableCell>
                  <TableCell>{gadget.ownerDetails?.name || '-'}</TableCell>
                  <TableCell>{gadget.ownerDetails?.telephone || '-'}</TableCell>
                  <TableCell>{gadget.ownerDetails?.address || '-'}</TableCell>
                  <TableCell>
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(gadget._id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Snackbar for alerts */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default GadgetList;
